// Proximity-aware meeting-date cell. For held meetings, mirrors the
// milestone schedule cell: scheduled date struck through, actual held
// date prominent, plus an early/on-time/late caption coloured by the
// per-cycle grace window. For other states: scheduled+future shows
// proximity ("in Nd" / "in N weeks"); scheduled+past shows the overdue
// suffix in red; cancelled shows muted with a "cancelled" tag.

import { clsx } from "clsx";
import type { Meeting, MeetingStatus } from "../../../../types";
import {
  meetingGraceDays,
  type MeetingGraceConfig,
} from "./meetingGraceDays";

export interface MeetingDateCellProps {
  status: MeetingStatus;
  date: string;
  heldDate?: string;
  recurrenceRule?: Meeting["recurrenceRule"];
  today?: Date;
  /** Per-contract override for the on-time grace window. Either a
   *  single number, a config object with per-frequency overrides, or
   *  null/undefined to use the cadence-proportional formula. */
  graceOverride?: number | MeetingGraceConfig | null;
}

type Tone = "danger" | "warning" | "success" | "brand" | "neutral" | "muted";

const TONE_CLASS: Record<Tone, string> = {
  danger: "text-red-600 dark:text-red-400 font-medium",
  warning: "text-amber-700 dark:text-amber-400",
  success: "text-emerald-700 dark:text-emerald-400",
  brand: "text-brand-700 dark:text-brand-400",
  neutral: "text-slate-700 dark:text-slate-200",
  muted: "text-slate-400 dark:text-slate-500",
};

function formatLongDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function daysBetweenUTC(fromIso: string, toIso: string): number {
  const a = new Date(`${fromIso}T00:00:00Z`);
  const b = new Date(`${toIso}T00:00:00Z`);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function daysFromTodayUTC(today: Date, toIso: string): number {
  const todayUtc = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const due = new Date(`${toIso}T00:00:00Z`);
  const dueUtc = Date.UTC(
    due.getUTCFullYear(),
    due.getUTCMonth(),
    due.getUTCDate(),
  );
  return Math.round((dueUtc - todayUtc) / 86_400_000);
}

function describeAhead(days: number): string {
  if (days < 14) return `${days}d ahead`;
  return `${Math.round(days / 7)} weeks ahead`;
}

function describeLate(days: number): string {
  if (days < 14) return `${days}d late`;
  return `${Math.round(days / 7)} weeks late`;
}

export default function MeetingDateCell({
  status,
  date,
  heldDate,
  recurrenceRule,
  today = new Date(),
  graceOverride,
}: MeetingDateCellProps) {
  // Held + heldDate present: milestone-style strikethrough rendering.
  if (status === "held" && heldDate) {
    const planned = formatLongDate(date);
    const actual = formatLongDate(heldDate);
    const slip = daysBetweenUTC(date, heldDate);
    const grace = meetingGraceDays(recurrenceRule, graceOverride);
    let metaTone: Tone;
    let meta: string;
    if (slip > grace) {
      metaTone = "warning";
      meta = describeLate(slip);
    } else if (slip < 0) {
      metaTone = "success";
      meta = `✓ early (${describeAhead(Math.abs(slip))})`;
    } else if (slip > 0) {
      metaTone = "success";
      meta = `✓ on time (${slip}d after)`;
    } else {
      metaTone = "success";
      meta = "✓ on time";
    }
    return (
      <div data-testid="meeting-date-cell" className="space-y-0.5">
        <div className="text-body">
          <span className="text-slate-400 line-through dark:text-slate-500">
            {planned}
          </span>
          <span className="ml-1 text-slate-700 dark:text-slate-200">
            → {actual}
          </span>
        </div>
        <div className={clsx("text-caption", TONE_CLASS[metaTone])}>
          {meta}
        </div>
      </div>
    );
  }

  // Held with no heldDate (legacy data): just show the scheduled date muted.
  if (status === "held") {
    return (
      <span
        data-testid="meeting-date-cell"
        className={TONE_CLASS.muted}
        title={date}
      >
        {formatLongDate(date)}
      </span>
    );
  }

  if (status === "cancelled") {
    return (
      <span
        data-testid="meeting-date-cell"
        className={TONE_CLASS.muted}
        title={date}
      >
        {formatShortDate(date)} · cancelled
      </span>
    );
  }

  // status === 'scheduled': split by proximity to today.
  const days = daysFromTodayUTC(today, date);
  const dateStr = formatShortDate(date);

  if (days < 0) {
    const overdueDays = Math.abs(days);
    return (
      <span
        data-testid="meeting-date-cell"
        className={TONE_CLASS.danger}
        title={date}
      >
        {dateStr} · {overdueDays}d overdue · not yet held
      </span>
    );
  }
  if (days === 0) {
    return (
      <span
        data-testid="meeting-date-cell"
        className={TONE_CLASS.brand}
        title={date}
      >
        {dateStr} · today
      </span>
    );
  }
  if (days <= 7) {
    return (
      <span
        data-testid="meeting-date-cell"
        className={TONE_CLASS.brand}
        title={date}
      >
        {dateStr} · in {days}d
      </span>
    );
  }
  if (days <= 30) {
    const weeks = Math.round(days / 7);
    return (
      <span
        data-testid="meeting-date-cell"
        className={TONE_CLASS.neutral}
        title={date}
      >
        {dateStr} · in {weeks} {weeks === 1 ? "week" : "weeks"}
      </span>
    );
  }
  const weeks = Math.round(days / 7);
  return (
    <span
      data-testid="meeting-date-cell"
      className={TONE_CLASS.neutral}
      title={date}
    >
      {dateStr} · in {weeks} weeks
    </span>
  );
}
